import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLayerGroup, faSitemap, faPen, faArrowRightLong, faBolt, faPlay, faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { SCHEMES, SRS_CELLS, SEMISTATIONS, LINES, WHOLE_STATION, transitionSteps, scopeHasGenerator, stateClassOf, stateLabelOf, isStateValidForScope } from "../../Functions/htScenarios";
import { Dropdown, OptGroup, StateChip, StatePicker, buildCellGroups } from "./CreateSessionFields";
import "./CreateTrainingSession.css";

// Sentinel cell id for free practice (no initial/final state, no scoring).
const FREE_ID = "free";

const CreateTrainingSessionForm = ({ data } : { data: any }) => {
    const navigate = useNavigate();
    const [cookies] = useCookies();
    const en = cookies?.language === "en";

    // Nothing preselected: every field starts empty and must be filled in.
    const [scheme, setScheme] = useState<string>("");
    const [initialState, setInitialState] = useState<string>("");
    const [finalState, setFinalState] = useState<string>("");
    const [cell, setCell] = useState<string>("");
    const [name, setName] = useState<string>("");
    const [nameTouched, setNameTouched] = useState<boolean>(false);
    const [busy, setBusy] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    // Set on a failed create attempt: the offending fields get a red outline
    // until they hold a usable value.
    const [showErrors, setShowErrors] = useState<boolean>(false);

    const isTeacher = data?.userData?.type === "teacher";
    const isFree = cell === FREE_ID;
    const bothStates = !!initialState && !!finalState;
    // Same operative class for start and finish -> nothing to do. Only meaningful
    // once both states are actually picked.
    const sameState = !isFree && bothStates && stateClassOf(initialState) === stateClassOf(finalState);
    // Every field the create needs (free practice needs no states).
    const missingFields = !scheme || !cell || (!isFree && !bothStates);

    const schemeGroups: OptGroup[] = useMemo(() => ([
        { items: SCHEMES.map((s) => ({ id: s.id, label: s.enabled ? s.label : `${s.label}${en ? " (soon)" : " (în curând)"}`, disabled: !s.enabled })) },
    ]), [en]);
    const cellGroups: OptGroup[] = useMemo(() => buildCellGroups(SRS_CELLS, LINES, en, [
        // Free practice: no target, no scoring. Pinned bottom-left (first column).
        { footer: true, group: en ? "Free practice" : "Antrenament liber", scope: true, items: [{ id: FREE_ID, label: en ? "Free roam" : "Liber" }] },
    ]), [en]);
    // Keep both states valid for the chosen cell. An already-picked state that
    // the new scope cannot reach is cleared, not silently swapped — the trainer
    // picks again. Nothing to constrain until a scope is picked.
    useEffect(() => {
        if (!cell) return;
        if (initialState && !isStateValidForScope(initialState, cell)) setInitialState("");
        if (finalState && !isStateValidForScope(finalState, cell)) setFinalState("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cell]);

    const effectiveCell = cell;
    // Display name of a scope/cell (cells use their label without the CEL code).
    const scopeDisplay = (id: string): string => {
        const c = SRS_CELLS.find((x) => x.id === id);
        if (c) return c.label.replace(/\s*\(.*\)$/, "");
        const opt = cellGroups.flatMap((g) => g.items).find((o) => o.id === id);
        return opt?.label || id;
    };
    // Only the parts already chosen make it into the auto name.
    const autoName = useMemo(() => (
        [scheme || null, effectiveCell ? scopeDisplay(effectiveCell) : null,
            bothStates ? `${stateLabelOf(initialState)} → ${stateLabelOf(finalState)}` : null]
            .filter(Boolean).join(" — ")
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ), [scheme, initialState, finalState, effectiveCell, bothStates]);
    useEffect(() => { if (!nameTouched) setName(autoName); }, [autoName, nameTouched]);

    const displayName = () => (name.trim() ? name.trim() : autoName);
    const steps = (!bothStates || sameState) ? [] : transitionSteps(stateClassOf(initialState), stateClassOf(finalState), scopeHasGenerator(cell));

    const createSession = async () => {
        if (busy) return;
        if (missingFields) {
            setShowErrors(true);
            return setError(en ? "Fill in every field first." : "Completează toate câmpurile.");
        }
        if (sameState) {
            setShowErrors(true);
            return setError(en ? "Initial and final state are the same — nothing to maneuver." : "Starea inițială și cea finală sunt identice — nu există manevre de efectuat.");
        }
        setShowErrors(false);
        setBusy(true); setError("");
        try {
            // Free practice runs on the whole station with no target; scenario "free".
            const sentCell = isFree ? WHOLE_STATION : effectiveCell;
            const semiStation = SEMISTATIONS.some((s) => s.id === sentCell) ? sentCell : "";
            // Persist both states in the `scenario` column as "initial>final".
            const scenario = isFree ? "free" : `${initialState}>${finalState}`;
            const request = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/session-httraining/create-session-httraining`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ userId: data.userData.userId, schemeName: scheme, semiStation, cell: sentCell, scenario, name: displayName(), problems: { scheme, cell: sentCell, initialState: isFree ? "" : initialState, finalState: isFree ? "" : finalState } }),
            });
            const response = await request.json();
            if (!response.success) { setError(en ? "Could not create the session." : "Sesiunea nu a putut fi creată."); setBusy(false); return; }
            const sName = displayName();
            window.history.replaceState({ sessionName: sName, ownerId: data.userData.userId }, "");
            if (isTeacher) navigate(`/session-httraining/${response.sessionId}/leaderboard`, { state: { sessionName: sName } });
            else navigate(`/session-httraining/${response.sessionId}/0`, { state: { sessionName: sName, ownerId: data.userData.userId } });
        } catch (e) {
            setError(en ? "Could not create the session." : "Sesiunea nu a putut fi creată."); setBusy(false);
        }
    };

    return (
        <div className="cts-form">
            <p className="cts-intro cts-span">
                {en
                    ? "Configure a training exercise on the SRS station: pick a cell, an initial state and a final state, then bring it there respecting the maneuver rules."
                    : "Configurează un exercițiu de antrenament pe stația SRS: alege o celulă, o stare inițială și o stare finală, apoi adu-o acolo respectând regulile de manevră."}
            </p>

            <Dropdown label={en ? "Station" : "Statie"} icon={faLayerGroup} value={scheme} groups={schemeGroups} onChange={setScheme}
                placeholder={en ? "Choose a station" : "Alege o stație"} invalid={showErrors && !scheme} />
            {/* Cell first: the chosen cell decides which states are available. */}
            <Dropdown label={en ? "Cell / semi-station" : "Celulă / semistație"} icon={faSitemap} value={cell} groups={cellGroups} onChange={setCell}
                placeholder={en ? "Choose a cell / semi-station" : "Alege o celulă / semistație"} invalid={showErrors && !cell} matrix />
            {/* State pickers are irrelevant for free practice. */}
            {!isFree ? (
                <>
                    <StatePicker label={en ? "Initial state" : "Starea inițială"} value={initialState} onChange={setInitialState}
                        isValid={(id) => !cell || isStateValidForScope(id, cell)}
                        placeholder={en ? "Choose the initial state" : "Alege starea inițială"}
                        invalid={showErrors && (!initialState || sameState)} anchor="left" />
                    <StatePicker label={en ? "Final state" : "Starea finală"} value={finalState} onChange={setFinalState}
                        isValid={(id) => !cell || isStateValidForScope(id, cell)}
                        placeholder={en ? "Choose the final state" : "Alege starea finală"}
                        invalid={showErrors && (!finalState || sameState)} anchor="right" />
                </>
            ) : null}
            <label className="cts-field cts-span">
                <span><FontAwesomeIcon icon={faPen} className="cts-label-icon" />{en ? "Session name" : "Numele sesiunii"}</span>
                <input type="text" value={name} placeholder={autoName || (en ? "Session name" : "Numele sesiunii")}
                    onChange={(e) => { setNameTouched(true); setName(e.target.value); }} />
            </label>

            {/* initial -> final transition visual */}
            <div className="cts-transition cts-span">
                <div className="cts-transition-head">
                    <FontAwesomeIcon icon={faCircleInfo} />
                    <span>{isFree
                        ? (en ? "Free practice" : "Antrenament liber")
                        : bothStates
                            ? `${stateLabelOf(initialState)} → ${stateLabelOf(finalState)}`
                            : (en ? "Transition" : "Tranzitia")}</span>
                </div>

                {isFree ? (
                    <p className="cts-transition-desc">
                        {en
                            ? "Practice freely on the whole station — no target state and no scoring. Interlock warnings still show."
                            : "Exersezi liber pe toata statia — fara stare tinta si fara punctaj. Avertismentele de interblocare apar in continuare."}
                    </p>
                ) : !bothStates ? (
                    <p className="cts-transition-desc">
                        {en
                            ? "Pick a cell, an initial state and a final state to see the maneuvers."
                            : "Alege o celula, o stare initiala si o stare finala pentru a vedea manevrele."}
                    </p>
                ) : sameState ? (
                    <p className="cts-transition-desc" style={{ color: "#c0392b", fontWeight: 700 }}>
                        {en ? "Initial and final state are the same — nothing to maneuver." : "Starea inițială și cea finală sunt identice — nu există manevre de efectuat."}
                    </p>
                ) : (
                    <>
                        <div className="cts-transition-flow">
                            <StateChip title={en ? "From" : "Din starea"} state={stateLabelOf(initialState)} />
                            <FontAwesomeIcon icon={faArrowRightLong} className="cts-transition-arrow" />
                            <StateChip title={en ? "To" : "În starea"} state={stateLabelOf(finalState)} />
                        </div>
                        {steps.length ? (
                            <div className="cts-transition-steps">
                                {steps.map((s) => (
                                    <div className="cts-step" key={s.key}>
                                        <FontAwesomeIcon icon={faBolt} className="cts-step-icon" />
                                        <span className="cts-step-label">{s.label}</span>
                                        <span className={`cts-badge ${s.from}`}>{s.from}</span>
                                        <FontAwesomeIcon icon={faArrowRightLong} className="cts-step-arrow" />
                                        <span className={`cts-badge ${s.to}`}>{s.to}</span>
                                    </div>
                                ))}
                            </div>
                        ) : null}
                    </>
                )}
            </div>

            {error ? <p className="cts-error cts-span">{error}</p> : null}

            <button type="button" className="cts-submit cts-span" disabled={busy} onClick={createSession}>
                <FontAwesomeIcon icon={faPlay} className="cts-submit-icon" />
                {busy ? (en ? "Creating..." : "Se creează...") : (en ? "Create session" : "Creează sesiunea")}
            </button>
        </div>
    );
};

export default CreateTrainingSessionForm;
